import { useState, useEffect, useCallback } from "react";

export interface CompletedTask {
  taskId: string;
  title: string;
  type: string;
  score: number;
  completedAt: number;
}

export interface PracticeRecord {
  durationMs: number;
  exercisesCount: number;
  stars: number;
  completedAt: number;
}

export interface StudentStats {
  tasksCompleted: number;
  practiceSessions: number;
  totalPracticeMinutes: number;
  averageScore: number;
  currentStreak: number;
  longestStreak: number;
  explorations: number;
  lastActiveDate: string | null;
}

const STATS_KEY = "bordona-student-stats";
const TASKS_KEY = "bordona-completed-tasks";
const PRACTICE_KEY = "bordona-practice-sessions";

function getTodayStr(): string {
  return new Date().toISOString().split("T")[0];
}

function loadJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveJSON<T>(key: string, value: T) {
  localStorage.setItem(key, JSON.stringify(value));
}

function calculateStreak(lastActiveDate: string | null): { current: number; longest: number } {
  const history = loadJSON<Record<string, boolean>>("bordona-active-days", {});
  const dates = Object.keys(history).sort();
  if (dates.length === 0) return { current: 0, longest: 0 };

  let longest = 0;
  let current = 0;
  let streak = 0;

  for (let i = 0; i < dates.length; i++) {
    if (i === 0) {
      streak = 1;
    } else {
      const prev = new Date(dates[i - 1]);
      const curr = new Date(dates[i]);
      const diff = (curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24);
      if (diff === 1) {
        streak++;
      } else {
        longest = Math.max(longest, streak);
        streak = 1;
      }
    }
  }
  longest = Math.max(longest, streak);

  // Calcular current desde hoy hacia atrás
  const today = getTodayStr();
  const todayDate = new Date(today);
  current = 0;
  for (let i = dates.length - 1; i >= 0; i--) {
    const d = new Date(dates[i]);
    const diff = Math.round((todayDate.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === current) {
      current++;
    } else if (diff > current) {
      break;
    }
  }

  return { current, longest };
}

function markActiveToday() {
  const history = loadJSON<Record<string, boolean>>("bordona-active-days", {});
  history[getTodayStr()] = true;
  saveJSON("bordona-active-days", history);
}

export function useStudentStats() {
  const [stats, setStats] = useState<StudentStats>(() => {
    const saved = loadJSON<StudentStats | null>(STATS_KEY, null);
    if (saved) {
      const { current, longest } = calculateStreak(saved.lastActiveDate);
      return { ...saved, currentStreak: current, longestStreak: longest };
    }
    return {
      tasksCompleted: 0,
      practiceSessions: 0,
      totalPracticeMinutes: 0,
      averageScore: 0,
      currentStreak: 0,
      longestStreak: 0,
      explorations: 0,
      lastActiveDate: null,
    };
  });

  const [completedTasks, setCompletedTasks] = useState<CompletedTask[]>(() =>
    loadJSON<CompletedTask[]>(TASKS_KEY, [])
  );

  const [practiceRecords, setPracticeRecords] = useState<PracticeRecord[]>(() =>
    loadJSON<PracticeRecord[]>(PRACTICE_KEY, [])
  );

  // Persistir stats
  useEffect(() => {
    saveJSON(STATS_KEY, stats);
  }, [stats]);

  useEffect(() => {
    saveJSON(TASKS_KEY, completedTasks);
  }, [completedTasks]);

  useEffect(() => {
    saveJSON(PRACTICE_KEY, practiceRecords);
  }, [practiceRecords]);

  const recordTaskCompletion = useCallback(
    (task: { taskId: string; title: string; type: string }, score: number) => {
      markActiveToday();
      const entry: CompletedTask = {
        taskId: task.taskId,
        title: task.title,
        type: task.type,
        score,
        completedAt: Date.now(),
      };
      setCompletedTasks((prev) => {
        const filtered = prev.filter((t) => t.taskId !== task.taskId);
        return [entry, ...filtered].slice(0, 50);
      });
      setStats((prev) => {
        const total = prev.tasksCompleted + 1;
        const newAvg =
          prev.averageScore === 0
            ? score
            : Math.round((prev.averageScore * prev.tasksCompleted + score) / total);
        const { current, longest } = calculateStreak(getTodayStr());
        return {
          ...prev,
          tasksCompleted: total,
          averageScore: newAvg,
          currentStreak: current,
          longestStreak: Math.max(prev.longestStreak, longest),
          lastActiveDate: getTodayStr(),
        };
      });
    },
    []
  );

  const recordPracticeSession = useCallback(
    (durationMs: number, exercisesCount: number, stars: number) => {
      markActiveToday();
      const entry: PracticeRecord = {
        durationMs,
        exercisesCount,
        stars,
        completedAt: Date.now(),
      };
      setPracticeRecords((prev) => [entry, ...prev].slice(0, 50));
      setStats((prev) => {
        const { current, longest } = calculateStreak(getTodayStr());
        return {
          ...prev,
          practiceSessions: prev.practiceSessions + 1,
          totalPracticeMinutes: Math.round((prev.totalPracticeMinutes + durationMs / 60000) * 10) / 10,
          currentStreak: current,
          longestStreak: Math.max(prev.longestStreak, longest),
          lastActiveDate: getTodayStr(),
        };
      });
    },
    []
  );

  const recordExploration = useCallback(() => {
    markActiveToday();
    setStats((prev) => {
      const { current, longest } = calculateStreak(getTodayStr());
      return {
        ...prev,
        explorations: prev.explorations + 1,
        currentStreak: current,
        longestStreak: Math.max(prev.longestStreak, longest),
        lastActiveDate: getTodayStr(),
      };
    });
  }, []);

  const getRecentTasks = useCallback(
    (limit = 5) => completedTasks.slice(0, limit),
    [completedTasks]
  );

  const getRecentPractice = useCallback(
    (limit = 5) => practiceRecords.slice(0, limit),
    [practiceRecords]
  );

  return {
    stats,
    completedTasks,
    practiceRecords,
    recordTaskCompletion,
    recordPracticeSession,
    recordExploration,
    getRecentTasks,
    getRecentPractice,
  };
}
